import { types, getParent, getRoot, getSnapshot, destroy } from 'mobx-state-tree';
import { v4 as uuidv4 } from 'uuid';
import { ImageElement } from './image-model';
import { TextElement } from './text-model';
import { SVGElement } from './svg-model';
import { FigureElement } from './figure-model';
import { LineElement } from './line-model';
import { GroupElement, ElementUnion, TYPES_MAP } from './group-model';

export const Page = types
  .model('Page', {
    id: types.optional(types.identifier, () => uuidv4()),
    children: types.optional(types.array(types.late(() => ElementUnion)), []),
    width: types.optional(types.union(types.number, types.literal('auto')), 'auto'),
    height: types.optional(types.union(types.number, types.literal('auto')), 'auto'),
    background: types.optional(types.string, ''),
    bleed: types.optional(types.number, 0),
    custom: types.optional(types.frozen(), {}),
    duration: types.optional(types.number, 5000),
  })
  .volatile(() => ({
    _exporting: false,
    _rendering: false,
    _forceMount: false,
  }))
  .views((self) => ({
    get store() {
      try {
        return getRoot(self);
      } catch (e) {
        return undefined;
      }
    },
    get computedWidth() {
      if (self.width !== 'auto') return self.width;
      const store = self.store;
      return store ? store.width : 500;
    },
    get computedHeight() {
      if (self.height !== 'auto') return self.height;
      const store = self.store;
      return store ? store.height : 500;
    },
    get startTime() {
      const store = self.store;
      if (!store) return 0;
      let time = 0;
      for (const page of store.pages) {
        if (page.id === self.id) return time;
        time += page.duration;
      }
      return time;
    },
    get _exportingOrRendering() {
      return self._exporting || self._rendering;
    },
  }))
  .actions((self) => ({
    toJSON() {
      return {
        id: self.id,
        children: self.children.map((child) => {
          if (child.toJSON) return child.toJSON();
          return getSnapshot(child);
        }),
        width: self.width,
        height: self.height,
        background: self.background,
        bleed: self.bleed,
        custom: self.custom,
        duration: self.duration,
      };
    },
    set(attrs) {
      for (const [key, value] of Object.entries(attrs)) {
        if (key in self && key !== 'id') {
          self[key] = value;
        }
      }
    },
    select() {
      const store = self.store;
      if (store) {
        store.selectPage(self.id);
      }
    },
    clone(attrs = {}) {
      const json = self.toJSON();
      delete json.id;
      Object.assign(json, attrs);
      const store = self.store;
      if (store) {
        return store.addPage(json);
      }
      return null;
    },
    addElement(attrs, { skipSelect = false } = {}) {
      const type = attrs.type;
      const elementData = {
        id: uuidv4(),
        ...attrs,
      };

      self.children.push(elementData);
      const newElement = self.children[self.children.length - 1];

      if (!skipSelect) {
        const store = self.store;
        if (store) {
          store.selectElements([newElement.id]);
        }
      }

      return newElement;
    },

    // Z-ordering helpers
    _findChildIndex(id) {
      return self.children.findIndex((c) => c.id === id);
    },

    canMoveElementsUp(ids) {
      for (const id of ids) {
        const idx = self._findChildIndex(id);
        if (idx < self.children.length - 1) return true;
      }
      return false;
    },
    moveElementsUp(ids) {
      for (const id of ids) {
        self.moveElementUp(id);
      }
    },
    moveElementUp(id) {
      const idx = self._findChildIndex(id);
      if (idx >= 0 && idx < self.children.length - 1) {
        const snapshot = getSnapshot(self.children[idx]);
        self.children.splice(idx, 1);
        self.children.splice(idx + 1, 0, snapshot);
      }
    },

    canMoveElementsTop(ids) {
      for (const id of ids) {
        const idx = self._findChildIndex(id);
        if (idx < self.children.length - 1) return true;
      }
      return false;
    },
    moveElementsTop(ids) {
      for (const id of ids) {
        self.moveElementTop(id);
      }
    },
    moveElementTop(id) {
      const idx = self._findChildIndex(id);
      if (idx >= 0 && idx < self.children.length - 1) {
        const snapshot = getSnapshot(self.children[idx]);
        self.children.splice(idx, 1);
        self.children.push(snapshot);
      }
    },

    canMoveElementsDown(ids) {
      for (const id of ids) {
        const idx = self._findChildIndex(id);
        if (idx > 0) return true;
      }
      return false;
    },
    moveElementsDown(ids) {
      for (const id of ids) {
        self.moveElementDown(id);
      }
    },
    moveElementDown(id) {
      const idx = self._findChildIndex(id);
      if (idx > 0) {
        const snapshot = getSnapshot(self.children[idx]);
        self.children.splice(idx, 1);
        self.children.splice(idx - 1, 0, snapshot);
      }
    },

    canMoveElementsBottom(ids) {
      for (const id of ids) {
        const idx = self._findChildIndex(id);
        if (idx > 0) return true;
      }
      return false;
    },
    moveElementsBottom(ids) {
      for (const id of ids) {
        self.moveElementBottom(id);
      }
    },
    moveElementBottom(id) {
      const idx = self._findChildIndex(id);
      if (idx > 0) {
        const snapshot = getSnapshot(self.children[idx]);
        self.children.splice(idx, 1);
        self.children.splice(0, 0, snapshot);
      }
    },

    setElementZIndex(id, zIndex) {
      const idx = self._findChildIndex(id);
      if (idx >= 0 && zIndex >= 0 && zIndex < self.children.length) {
        const snapshot = getSnapshot(self.children[idx]);
        self.children.splice(idx, 1);
        self.children.splice(zIndex, 0, snapshot);
      }
    },

    setSize({ width, height, useMagic = false, softChange = false }) {
      if (width !== undefined) self.width = width;
      if (height !== undefined) self.height = height;
    },

    setZIndex(zIndex) {
      const store = self.store;
      if (store) {
        store.setPageZIndex(self.id, zIndex);
      }
    },

    _forEachElementUp(ids, callback) {
      for (let i = self.children.length - 1; i >= 0; i--) {
        const child = self.children[i];
        if (ids.includes(child.id)) {
          callback(child);
        }
      }
    },

    _forEachElementDown(ids, callback) {
      for (let i = 0; i < self.children.length; i++) {
        const child = self.children[i];
        if (ids.includes(child.id)) {
          callback(child);
        }
      }
      return false;
    },

    play() {
      // placeholder for animation support
    },
  }));
